/**
 * features/tax-invoices/hooks.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaxInvoiceInput, TaxInvoiceStatus } from '@ft/core';
import { api, queryString } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { Paginated, TaxInvoice } from '~/lib/types';

export interface TaxInvoiceListParams {
  page: number;
  pageSize: number;
  search: string;
  status?: TaxInvoiceStatus;
  dateFrom?: string;
  dateTo?: string;
}

export function useTaxInvoices(params: TaxInvoiceListParams) {
  return useQuery({
    queryKey: queryKeys.taxInvoices(params),
    queryFn: () =>
      api.get<Paginated<TaxInvoice>>(
        `/api/tax-invoices${queryString({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          status: params.status,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useTaxInvoice(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.taxInvoice(id ?? ''),
    queryFn: () => api.get<TaxInvoice>(`/api/tax-invoices/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateTaxInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxInvoiceInput) => api.post<TaxInvoice>('/api/tax-invoices', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tax-invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateTaxInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TaxInvoiceInput }) =>
      api.put<TaxInvoice>(`/api/tax-invoices/${id}`, input),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['tax-invoices'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.taxInvoice(variables.id) });
    },
  });
}

export function useDeleteTaxInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/tax-invoices/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tax-invoices'] });
      // ลบใบกำกับแล้วใบเสนอราคาต้นทางกลับเป็น draft ต้อง invalidate ด้วย
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
    },
  });
}
